import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { PreboardingFormSection } from '@hygge/shared';

export interface SectionDraft {
  title: string;
  description: string | null;
}

interface SectionEditorDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  section?: PreboardingFormSection | null;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (draft: SectionDraft) => void;
}

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 2000;

export function SectionEditorDialog({
  open,
  mode,
  section,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: SectionEditorDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      setTitle(section?.title ?? '');
      setDescription(section?.description ?? '');
    }
  }, [open, section]);

  const trimmedTitle = title.trim();
  const canSubmit =
    !isSubmitting &&
    trimmedTitle.length > 0 &&
    trimmedTitle.length <= TITLE_MAX &&
    description.length <= DESCRIPTION_MAX;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ title: trimmedTitle, description: description.trim() || null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? 'Add section' : 'Edit section'}</DialogTitle>
            <DialogDescription>
              A logical block of the form, e.g. “Personal Information” or “Bank Details”.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="section-title">Section title</Label>
              <Input
                id="section-title"
                autoFocus
                value={title}
                maxLength={TITLE_MAX}
                placeholder="e.g. Bank Details"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="section-description">Description / hint (optional)</Label>
              <Textarea
                id="section-description"
                rows={2}
                value={description}
                maxLength={DESCRIPTION_MAX}
                placeholder="Shown under the section title to guide the candidate."
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mode === 'create' ? 'Add section' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
